import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Alert,
  Button,
  Stack,
  Tooltip,
  Grid,
  Paper
} from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonSearch as PersonSearchIcon,
  Email as EmailIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { getStrugglingStudents } from '../../services/teacherService';
import { StrugglingStudent, StrugglingFlag } from '../../types/teacher';
import ContactStudentDialog from './ContactStudentDialog';
import StudentProgressDialog from './StudentProgressDialog';

interface StrugglingStudentsCardProps {
  refreshTrigger?: number;
}

const StrugglingStudentsCard: React.FC<StrugglingStudentsCardProps> = ({ refreshTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [strugglingStudents, setStrugglingStudents] = useState<StrugglingStudent[]>([]);
  const [expandedStudents, setExpandedStudents] = useState<Set<number>>(new Set());
  const [filterSeverity, setFilterSeverity] = useState<'all' | 'red' | 'yellow'>('all');
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    fetchStrugglingStudents();
  }, [refreshTrigger]);

  const fetchStrugglingStudents = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStrugglingStudents();
      setStrugglingStudents(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load struggling students';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const toggleStudentExpansion = (studentId: number) => {
    setExpandedStudents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const getFlagLabel = (type: StrugglingFlag['type']) => {
    switch (type) {
      case 'low_accuracy':
        return 'Low Accuracy';
      case 'no_activity':
        return 'No Activity';
      case 'declining_trend':
        return 'Declining Trend';
      case 'low_engagement':
        return 'Low Engagement';
      default:
        return type;
    }
  };

  const getMetricIconAndValue = (flag: StrugglingFlag) => {
    switch (flag.type) {
      case 'low_accuracy':
        return { icon: <TrendingDownIcon fontSize="small" />, label: 'Accuracy', value: `${flag.value}%` };
      case 'no_activity':
        return { icon: <ScheduleIcon fontSize="small" />, label: 'Days Inactive', value: `${flag.value} days` };
      case 'declining_trend':
        return { icon: <TrendingDownIcon fontSize="small" />, label: 'Trend', value: `${flag.value}% decline` };
      case 'low_engagement':
        return { icon: <AssignmentIcon fontSize="small" />, label: 'Submissions', value: flag.value };
      default:
        return { icon: null, label: '', value: flag.value };
    }
  };

  const exportToCSV = () => {
    const headers = ['Student Name', 'Severity', 'Issues', 'Recommended Action'];
    const rows = strugglingStudents.map(student => [
      student.studentName,
      student.flags.some(f => f.severity === 'red') ? 'Critical' : 'At Risk',
      student.flags.map(f => `${getFlagLabel(f.type)}: ${f.message}`).join('; '),
      student.recommendedAction
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `struggling-students-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Separate by severity
  const redFlagStudents = strugglingStudents.filter((s) =>
    s.flags.some((f) => f.severity === 'red')
  );
  const yellowFlagStudents = strugglingStudents.filter(
    (s) => !s.flags.some((f) => f.severity === 'red')
  );

  return (
    <Card>
      <CardHeader
        avatar={<PersonSearchIcon />}
        title="Students Needing Attention"
        subheader="Monitor students who may need additional support or intervention"
        action={
          strugglingStudents.length > 0 && (
            <Tooltip title="Export intervention list">
              <IconButton onClick={exportToCSV} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )
        }
      />
      <CardContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && !error && strugglingStudents.length === 0 && (
          <Alert severity="success">
            All students are performing well! No intervention needed at this time.
          </Alert>
        )}

        {!loading && !error && strugglingStudents.length > 0 && (
          <Box>
            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'error.light',
                    borderLeft: 4,
                    borderColor: 'error.main'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <ErrorIcon color="error" />
                    <Box>
                      <Typography variant="h4" color="error.dark" fontWeight="bold">
                        {redFlagStudents.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Critical
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'warning.light',
                    borderLeft: 4,
                    borderColor: 'warning.main'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <WarningIcon color="warning" />
                    <Box>
                      <Typography variant="h4" color="warning.dark" fontWeight="bold">
                        {yellowFlagStudents.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        At Risk
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'grey.100',
                    borderLeft: 4,
                    borderColor: 'grey.400'
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <PersonSearchIcon color="action" />
                    <Box>
                      <Typography variant="h4" fontWeight="bold">
                        {strugglingStudents.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Students
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Filter Controls */}
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={1}>
                <Chip
                  label="All Students"
                  onClick={() => setFilterSeverity('all')}
                  color={filterSeverity === 'all' ? 'primary' : 'default'}
                  variant={filterSeverity === 'all' ? 'filled' : 'outlined'}
                />
                <Chip
                  label={`Critical (${redFlagStudents.length})`}
                  onClick={() => setFilterSeverity('red')}
                  color={filterSeverity === 'red' ? 'error' : 'default'}
                  variant={filterSeverity === 'red' ? 'filled' : 'outlined'}
                  icon={<ErrorIcon />}
                />
                <Chip
                  label={`At Risk (${yellowFlagStudents.length})`}
                  onClick={() => setFilterSeverity('yellow')}
                  color={filterSeverity === 'yellow' ? 'warning' : 'default'}
                  variant={filterSeverity === 'yellow' ? 'filled' : 'outlined'}
                  icon={<WarningIcon />}
                />
              </Stack>
            </Box>

            {/* Critical Students Section */}
            {filterSeverity !== 'yellow' && redFlagStudents.length > 0 && (
              <Box mb={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <ErrorIcon color="error" />
                  <Typography variant="h6" color="error">
                    Critical: {redFlagStudents.length} Student{redFlagStudents.length > 1 ? 's' : ''} Need Immediate Attention
                  </Typography>
                </Box>

                <List disablePadding>
                  {redFlagStudents.map((student, index) => (
                    <React.Fragment key={student.studentId}>
                      <ListItem
                        disablePadding
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'error.dark' : 'error.light',
                          borderRadius: 1,
                          p: 2,
                          mb: index < redFlagStudents.length - 1 ? 1 : 0
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          onClick={() => toggleStudentExpansion(student.studentId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight="bold" color="error.dark">
                                {student.studentName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {student.recommendedAction}
                              </Typography>
                            }
                          />
                          <IconButton size="small">
                            {expandedStudents.has(student.studentId) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </Box>

                        <Collapse
                          in={expandedStudents.has(student.studentId)}
                          timeout="auto"
                          unmountOnExit
                          sx={{ width: '100%', mt: 2 }}
                        >
                          {/* Flag details with metrics */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
                              Performance Metrics
                            </Typography>
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {student.flags.map((flag, idx) => {
                                const metric = getMetricIconAndValue(flag);
                                return (
                                  <Paper
                                    key={idx}
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: flag.severity === 'red' ? 'error.lighter' : 'warning.lighter',
                                      borderLeft: 3,
                                      borderColor: flag.severity === 'red' ? 'error.main' : 'warning.main'
                                    }}
                                  >
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                      <Box display="flex" alignItems="center" gap={1}>
                                        {metric.icon}
                                        <Box>
                                          <Typography variant="body2" fontWeight="bold">
                                            {getFlagLabel(flag.type)}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {flag.message}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Chip
                                        label={metric.value}
                                        size="small"
                                        color={flag.severity === 'red' ? 'error' : 'warning'}
                                      />
                                    </Box>
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </Box>

                          {/* Action buttons */}
                          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EmailIcon />}
                              onClick={() => {
                                setSelectedStudent({ id: student.studentId, name: student.studentName });
                                setContactDialogOpen(true);
                              }}
                            >
                              Contact
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AssignmentIcon />}
                              onClick={() => {
                                setSelectedStudent({ id: student.studentId, name: student.studentName });
                                setProgressDialogOpen(true);
                              }}
                            >
                              View Progress
                            </Button>
                          </Stack>
                        </Collapse>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}

            {/* At Risk Students Section */}
            {filterSeverity !== 'red' && yellowFlagStudents.length > 0 && (
              <Box>
                {filterSeverity === 'all' && redFlagStudents.length > 0 && <Divider sx={{ my: 3 }} />}

                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <WarningIcon color="warning" />
                  <Typography variant="h6" color="warning.dark">
                    At Risk: {yellowFlagStudents.length} Student{yellowFlagStudents.length > 1 ? 's' : ''} to Monitor
                  </Typography>
                </Box>

                <List disablePadding>
                  {yellowFlagStudents.map((student, index) => (
                    <React.Fragment key={student.studentId}>
                      <ListItem
                        disablePadding
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'warning.dark' : 'warning.light',
                          borderRadius: 1,
                          p: 2,
                          mb: index < yellowFlagStudents.length - 1 ? 1 : 0
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          width="100%"
                          onClick={() => toggleStudentExpansion(student.studentId)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <ListItemText
                            primary={
                              <Typography variant="subtitle1" fontWeight="bold" color="warning.dark">
                                {student.studentName}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {student.recommendedAction}
                              </Typography>
                            }
                          />
                          <IconButton size="small">
                            {expandedStudents.has(student.studentId) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        </Box>

                        <Collapse
                          in={expandedStudents.has(student.studentId)}
                          timeout="auto"
                          unmountOnExit
                          sx={{ width: '100%', mt: 2 }}
                        >
                          {/* Flag details with metrics */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
                              Performance Metrics
                            </Typography>
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              {student.flags.map((flag, idx) => {
                                const metric = getMetricIconAndValue(flag);
                                return (
                                  <Paper
                                    key={idx}
                                    elevation={0}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: flag.severity === 'red' ? 'error.lighter' : 'warning.lighter',
                                      borderLeft: 3,
                                      borderColor: flag.severity === 'red' ? 'error.main' : 'warning.main'
                                    }}
                                  >
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                      <Box display="flex" alignItems="center" gap={1}>
                                        {metric.icon}
                                        <Box>
                                          <Typography variant="body2" fontWeight="bold">
                                            {getFlagLabel(flag.type)}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {flag.message}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Chip
                                        label={metric.value}
                                        size="small"
                                        color={flag.severity === 'red' ? 'error' : 'warning'}
                                      />
                                    </Box>
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </Box>

                          {/* Action buttons */}
                          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EmailIcon />}
                              onClick={() => {
                                setSelectedStudent({ id: student.studentId, name: student.studentName });
                                setContactDialogOpen(true);
                              }}
                            >
                              Contact
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AssignmentIcon />}
                              onClick={() => {
                                setSelectedStudent({ id: student.studentId, name: student.studentName });
                                setProgressDialogOpen(true);
                              }}
                            >
                              View Progress
                            </Button>
                          </Stack>
                        </Collapse>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Contact Dialog */}
      {selectedStudent && (
        <ContactStudentDialog
          open={contactDialogOpen}
          onClose={() => setContactDialogOpen(false)}
          studentName={selectedStudent.name}
          studentId={selectedStudent.id}
        />
      )}

      {/* Progress Dialog */}
      {selectedStudent && (
        <StudentProgressDialog
          open={progressDialogOpen}
          onClose={() => setProgressDialogOpen(false)}
          studentId={selectedStudent.id}
          studentName={selectedStudent.name}
        />
      )}
    </Card>
  );
};

export default StrugglingStudentsCard;
